import React, { useMemo, useState } from 'react';
import PrinterQuickConnect from './PrinterQuickConnect';
import PrinterPageEditor from './PrinterPageEditor';
import { usePrinterService } from '../../hooks/usePrinterService';

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const capabilityLabel = (label, enabled) => (
  <div className="flex items-center text-sm">
    <span className={`mr-2 text-lg ${enabled ? 'text-green-600' : 'text-gray-400'}`}>
      {enabled ? '✅' : '⚪'}
    </span>
    <span>{label}</span>
  </div>
);

const platformNotes = {
  web: [
    'Uses Web Bluetooth (BLE). Works in Chrome-based browsers on Desktop/Android.',
    'Printer must advertise ESC/POS characteristics (FFE0/FFE1).',
    'Browser requires a secure context (https:// or localhost).',
  ],
  electron: [
    'Prints via native OS printers (USB/Bluetooth).',
    'Install printer drivers in the OS and ensure a test page prints from system settings.',
    'Supports raw ESC/POS output via the system spooler.',
  ],
  'capacitor-android': [
    'Supports Bluetooth Classic (default) plus BLE fallback. Pair Classic printers in Android settings for fastest discovery.',
    'Grant Nearby Devices + Bluetooth permissions on Android 12+ when prompted.',
    'BLE fallback still targets ESC/POS UUIDs (FFE0/FFE1); adjust if your printer exposes different characteristics.',
  ],
  default: [
    'Printer service is running in fallback mode.',
    'Connect using the platform-specific instructions or use the PDF/Browser print fallback.',
  ],
};

const EmptyQueue = () => (
  <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-md p-3">
    No pending print jobs. Offline invoices will appear here for manual retry.
  </div>
);

const QueueList = ({ jobs = [] }) => {
  if (!jobs.length) {
    return <EmptyQueue />;
  }
  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
      {jobs.map((job) => (
        <li key={job.id} className="p-3 text-sm flex flex-col gap-1 bg-white">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-800">
              {job.printable?.type === 'raw' ? 'Raw ESC/POS Payload' : 'Invoice Document'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              job.status === 'failed' ? 'bg-red-100 text-red-700' :
              job.status === 'completed' ? 'bg-green-100 text-green-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {job.status || 'pending'}
            </span>
          </div>
          <div className="text-xs text-gray-500 font-mono break-all">
            {job.id}
          </div>
          <div className="flex flex-wrap text-xs text-gray-500 gap-3">
            <span>Created: {formatDateTime(job.createdAt)}</span>
            {job.completedAt && <span>Completed: {formatDateTime(job.completedAt)}</span>}
            {job.options?.reason && <span>Reason: {job.options.reason}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
};

const PrinterSetup = () => {
  const {
    state,
    flushQueue,
  } = usePrinterService();
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPrinterEditor, setShowPrinterEditor] = useState(false);

  const capabilities = state.capabilities || {};
  const queue = Array.isArray(state.queue) ? state.queue : [];
  const remoteHost = state.remoteHost || {};
  const hostIdentity = state.hostIdentity || {};
  const remoteStatus = remoteHost.status || (remoteHost.supported ? 'idle' : 'unsupported');

  const platform = useMemo(() => {
    if (state.environment === 'electron') return 'electron';
    if (state.environment?.startsWith('capacitor')) return state.environment;
    if (state.environment === 'web') return 'web';
    return 'default';
  }, [state.environment]);

  const notes = platformNotes[platform] || platformNotes.default;

  const handleFlush = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await flushQueue();
      if (result.failed > 0) {
        setMessage(`Processed ${result.processed} job(s). ${result.failed} failed. Ensure the printer is connected.`);
      } else {
        setMessage(`Processed ${result.processed} job(s).`);
      }
    } catch (error) {
      setMessage(error.message || 'Failed to process queue.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="bg-white border border-green-200 rounded-lg shadow-sm p-4 sm:p-5">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              🖨️ Thermal Printer Setup
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Connect a USB, Bluetooth, or BLE thermal printer. The connection is shared across Fast Billing, Billing, and offline queues.
            </p>
          </div>
          <button
            onClick={() => setShowPrinterEditor(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <span>⚙️</span>
            <span>Customize Print Layout</span>
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PrinterQuickConnect />

        <div className="bg-white border border-green-200 rounded-lg p-4 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Environment</h3>
            <dl className="mt-2 space-y-1 text-sm text-gray-600">
              <div className="flex justify-between">
                <dt>Runtime</dt>
                <dd className="font-mono text-gray-800">{state.environment || 'unknown'}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Current printer</dt>
                <dd className="font-medium text-gray-800">
                  {state.device?.name || state.persisted?.device?.name || 'Not connected'}
                </dd>
              </div>
              {state.device && (
                <div className="flex justify-between">
                  <dt>Connection</dt>
                  <dd className="text-gray-800">
                    {state.device.transport === 'bluetooth' ? (
                      <span className="inline-flex items-center gap-1">
                        <span>📶</span>
                        <span>Bluetooth</span>
                      </span>
                    ) : state.device.transport === 'usb' ? (
                      <span className="inline-flex items-center gap-1">
                        <span>🔌</span>
                        <span>USB</span>
                      </span>
                    ) : (
                      <span>{state.device.transport || 'Unknown'}</span>
                    )}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt>Last connected</dt>
                <dd>{formatDateTime(state.lastConnectedAt)}</dd>
              </div>
            </dl>
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-gray-800">Capabilities</h4>
            <div className="space-y-1">
              {capabilityLabel('Discover devices', capabilities.canDiscover)}
              {capabilityLabel('Pair / remember printer', capabilities.persistentConnection)}
              {capabilityLabel('Print raw ESC/POS', capabilities.canPrint)}
            </div>
            {Array.isArray(capabilities.notes) && capabilities.notes.length > 0 && (
              <ul className="mt-2 text-xs text-gray-500 list-disc list-inside space-y-1">
                {capabilities.notes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Queued jobs</h4>
              <button
                type="button"
                onClick={handleFlush}
                disabled={busy || !queue.length}
                className="text-xs px-3 py-1 rounded-md border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? 'Processing…' : 'Flush Queue'}
              </button>
            </div>
            <QueueList jobs={queue} />
          </div>
          {message && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
              {message}
            </div>
          )}
        </div>
      </div>

      {remoteHost.supported && (
        <div className="bg-white border border-purple-200 rounded-lg p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Desktop Bridge</h3>
              <p className="text-xs text-gray-500">
                Keeps this desktop signed in so bills from mobile/web can auto-print on the connected thermal printer.
              </p>
            </div>
            <span
              className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-semibold ${
                remoteStatus === 'listening'
                  ? 'bg-green-100 text-green-700'
                  : remoteStatus === 'printing'
                    ? 'bg-blue-100 text-blue-700'
                    : remoteStatus === 'error'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600'
              }`}
            >
              {remoteStatus === 'listening' && 'Listening'}
              {remoteStatus === 'printing' && 'Printing'}
              {remoteStatus === 'registering' && 'Registering'}
              {remoteStatus === 'idle' && 'Idle'}
              {remoteStatus === 'error' && 'Needs attention'}
              {remoteStatus === 'unsupported' && 'Unavailable'}
            </span>
          </div>
          <dl className="text-sm text-gray-700 space-y-1">
            <div className="flex justify-between">
              <dt>Host ID</dt>
              <dd className="font-mono text-xs">{hostIdentity.deviceId || 'unassigned'}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Desktop name</dt>
              <dd>{remoteHost.deviceName || hostIdentity.deviceName || 'Desktop session'}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Printer</dt>
              <dd>{remoteHost.printerName || state.device?.name || 'Select a printer to enable bridge'}</dd>
            </div>
          </dl>
          {remoteHost.lastJob && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
              <div className="font-semibold text-gray-700">Last job</div>
              <div className="flex justify-between">
                <span>{remoteHost.lastJob.title || remoteHost.lastJob.jobId}</span>
                <span className="font-mono text-gray-500">{remoteHost.lastJob.status}</span>
              </div>
            </div>
          )}
          {remoteHost.error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md p-2">
              {remoteHost.error}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-green-200 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-800">Platform notes</h3>
        <p className="text-xs text-gray-500">
          These instructions depend on the platform TrackProfit is running on.
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
          {notes.map((note, idx) => (
            <li key={idx}>{note}</li>
          ))}
        </ul>
        <div className="text-xs text-gray-500">
          Tip: You can always fall back to PDF download or browser printing from Fast Billing if a thermal printer is unavailable.
        </div>
      </div>

      {/* Printer Page Editor Modal */}
      <PrinterPageEditor
        open={showPrinterEditor}
        onClose={() => setShowPrinterEditor(false)}
      />
    </div>
  );
};

export default PrinterSetup;



